import type { GameConfig } from '@youraiquiz/shared';
import { getMaxTotalRemovals } from '@youraiquiz/shared';

interface Props {
  config: GameConfig;
  playerCount: number;
  onUpdate: (config: Partial<GameConfig>) => void;
}

const AdminPanelView = ({ config, playerCount, onUpdate }: Props) => {
  const maxRemovals = getMaxTotalRemovals(playerCount, config.categoriesPerPlayer);
  const maxVeto = Math.max(0, maxRemovals - config.nicheRemovalCount);
  const maxNiche = Math.max(0, maxRemovals - config.vetoRemovalCount);

  // Wrap onUpdate to auto-clamp removals when categoriesPerPlayer changes
  const handleUpdate = (update: Partial<GameConfig>) => {
    if (update.categoriesPerPlayer !== undefined) {
      const newMax = getMaxTotalRemovals(playerCount, update.categoriesPerPlayer);
      const totalRemovals = config.vetoRemovalCount + config.nicheRemovalCount;
      if (totalRemovals > newMax) {
        // Clamp both proportionally, favoring veto
        const newVeto = Math.min(config.vetoRemovalCount, newMax);
        const newNiche = Math.min(config.nicheRemovalCount, newMax - newVeto);
        update = { ...update, vetoRemovalCount: newVeto, nicheRemovalCount: newNiche };
      }
    }
    onUpdate(update);
  };

  const Control = ({
    label,
    description,
    value,
    field,
    min = 0,
    max = 20,
    step = 1
  }: {
    label: string;
    description: string;
    value: number;
    field: keyof GameConfig;
    min?: number;
    max?: number;
    step?: number;
  }) => (
    <div className="flex flex-col gap-1 glass p-4 rounded-2xl border-l-4 border-neon-purple transition-all hover:bg-white/10 text-left">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-[10px] font-black uppercase text-neon-purple tracking-widest">
            {label}
          </span>
          <div className="text-xl font-black text-white">{value}</div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleUpdate({ [field]: Math.max(min, value - step) })}
            className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center font-black text-xl active:bg-white/20 transition-colors"
          >
            -
          </button>
          <button
            type="button"
            onClick={() => handleUpdate({ [field]: Math.min(max, value + step) })}
            className="w-10 h-10 rounded-xl bg-neon-purple text-white flex items-center justify-center font-black text-xl active:scale-95 shadow-lg shadow-neon-purple/20"
          >
            +
          </button>
        </div>
      </div>
      <p className="text-[9px] font-bold text-white/40 leading-tight uppercase tracking-tight mt-1">
        {description}
      </p>
    </div>
  );

  return (
    <div className="space-y-4 w-full">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">🛠️</span>
        <h3 className="font-black uppercase tracking-[0.3em] text-[10px] text-white/60">
          Game Architecture
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Control
          label="Entries/Player"
          description="How many categories each player submits at the start."
          value={config.categoriesPerPlayer}
          field="categoriesPerPlayer"
          min={1}
          max={5}
        />
        <Control
          label="Skulls/Player"
          description="How many 'veto' skulls each player can spend to kill categories."
          value={config.votesPerPlayer}
          field="votesPerPlayer"
          min={1}
          max={10}
        />
        <Control
          label="Skull Removals"
          description={`Categories purged by skull votes. Max ${maxVeto} with ${playerCount} players.`}
          value={config.vetoRemovalCount}
          field="vetoRemovalCount"
          min={0}
          max={maxVeto}
        />
        <Control
          label="Niche Purge"
          description={`AI removes the most obscure categories. Max ${maxNiche} with ${playerCount} players.`}
          value={config.nicheRemovalCount}
          field="nicheRemovalCount"
          min={0}
          max={maxNiche}
        />
        <Control
          label="Quiz Length"
          description="Total questions. Every surviving category is guaranteed representation."
          value={config.maxTotalQuestions}
          field="maxTotalQuestions"
          min={3}
          max={60}
          step={3}
        />
      </div>

      {/* Dev Mode Toggle */}
      <div className="mt-6 pt-4 border-t border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg">🧪</span>
          <h3 className="font-black uppercase tracking-[0.3em] text-[10px] text-white/60">
            Debug Options
          </h3>
        </div>
        <div
          className={`flex items-center justify-between glass p-4 rounded-2xl border-l-4 transition-all cursor-pointer ${
            config.devMode ? 'border-amber-400 bg-amber-400/10' : 'border-white/20'
          }`}
          onClick={() => handleUpdate({ devMode: !config.devMode })}
        >
          <div className="flex flex-col text-left">
            <span className={`text-[10px] font-black uppercase tracking-widest ${
              config.devMode ? 'text-amber-400' : 'text-white/40'
            }`}>
              Dev Mode
            </span>
            <p className="text-[9px] font-bold text-white/40 leading-tight uppercase tracking-tight mt-1">
              Removes question timer. Round ends when all players answer.
            </p>
          </div>
          <div
            className={`w-14 h-8 rounded-full p-1 transition-all ${
              config.devMode ? 'bg-amber-400' : 'bg-white/10'
            }`}
          >
            <div
              className={`w-6 h-6 rounded-full bg-white shadow-lg transition-transform ${
                config.devMode ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanelView;
